import React from 'react';
import PublicLayout from '../components/PublicLayout';
import SEO from '../components/SEO';
import { Shield, Eye, Lock, Database, Globe, Mail, Clock } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <PublicLayout>
      <SEO 
        title="Privacy Policy | LetWise"
        description="Privacy Policy for C&C LET MANAGEMENT LIMITED trading as LetWise. How we collect, use, and protect your personal data."
      />
      
      <div className="bg-slate-50 min-h-screen">
        {/* Header */}
        <div className="bg-primary text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Shield className="w-16 h-16 mx-auto mb-6 opacity-80" />
              <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
              <p className="text-xl opacity-90">
                C&C LET MANAGEMENT LIMITED trading as LetWise
              </p>
              <p className="text-sm mt-4 opacity-75">
                Last updated: January 2026
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto space-y-8">
            
            {/* Introduction */}
            <section className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Introduction</h2>
              <p className="text-slate-600 mb-4">
                C&C LET MANAGEMENT LIMITED (Company No. SC869470), trading as LetWise ("we", "us", "our"), 
                is committed to protecting your privacy. This policy explains how we collect, use, and 
                protect your personal data in accordance with the UK General Data Protection Regulation 
                (UK GDPR) and the Data Protection Act 2018.
              </p>
              <p className="text-slate-600">
                <strong>Data Controller:</strong> C&C LET MANAGEMENT LIMITED<br />
                <strong>Registered Address:</strong> Unit 4, 416 Hamilton Road, Cambuslang, Glasgow, Scotland, G72 7XR<br />
                <strong>Contact:</strong> privacy@letwise.io
              </p>
            </section>

            {/* What We Collect */}
            <section className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Database className="w-6 h-6 text-primary" />
                2. Information We Collect
              </h2>
              <div className="space-y-4 text-slate-600">
                <h3 className="font-semibold text-slate-900">Personal Information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Name, email address, phone number</li>
                  <li>Postal address and property details</li>
                  <li>Date of birth and identification documents</li>
                  <li>Employment and income information (for referencing)</li>
                  <li>Bank account details (for rent payments)</li>
                  <li>Previous landlord references</li>
                </ul>
                
                <h3 className="font-semibold text-slate-900 mt-6">Technical Information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>IP address and browser type</li>
                  <li>Device information</li>
                  <li>Login and usage data</li>
                  <li>Cookies and similar technologies</li>
                </ul>
              </div>
            </section>

            {/* How We Use Data */}
            <section className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Eye className="w-6 h-6 text-primary" />
                3. How We Use Your Information
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>We use your personal data to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide property management services</li>
                  <li>Process tenancy applications and references</li>
                  <li>Manage rent collection and payments</li>
                  <li>Communicate about your tenancy or property</li>
                  <li>Comply with legal and regulatory requirements</li>
                  <li>Protect deposits with approved schemes</li>
                  <li>Coordinate maintenance and repairs</li>
                  <li>Send service updates and notifications</li>
                  <li>Improve our services</li>
                </ul>
              </div>
            </section>

            {/* Legal Basis */}
            <section className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Legal Basis for Processing</h2>
              <div className="space-y-4 text-slate-600">
                <p>We process your data under the following legal bases:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Contract:</strong> To perform our obligations under tenancy or management agreements</li>
                  <li><strong>Legal Obligation:</strong> To comply with housing, financial, and data protection laws</li>
                  <li><strong>Legitimate Interests:</strong> To operate our business, prevent fraud, and improve services</li>
                  <li><strong>Consent:</strong> For marketing communications (you can withdraw at any time)</li>
                </ul>
              </div>
            </section>

            {/* Data Sharing */}
            <section className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Globe className="w-6 h-6 text-primary" />
                5. Data Sharing
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>We may share your data with:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Credit reference agencies (for tenant referencing)</li>
                  <li>Deposit protection schemes (MyDeposits, Safe Deposit Scotland)</li>
                  <li>Landlords (relevant tenancy information)</li>
                  <li>Contractors (for property maintenance)</li>
                  <li>Payment processors (for secure transactions)</li>
                  <li>Legal advisors and regulatory bodies (when required)</li>
                  <li>Local authorities (for licensing and compliance)</li>
                </ul>
                <p className="mt-4">
                  We do not sell your personal data to third parties for marketing purposes.
                </p>
              </div>
            </section>

            {/* Data Retention */}
            <section className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Clock className="w-6 h-6 text-primary" />
                6. Data Retention
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>We retain personal data for as long as necessary to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide our services</li>
                  <li>Comply with legal obligations (typically 6 years for financial records)</li>
                  <li>Resolve disputes</li>
                  <li>Enforce our agreements</li>
                </ul>
                <p className="mt-4">
                  Tenancy records are typically retained for 6 years after the end of a tenancy. 
                  We securely delete data when no longer required.
                </p>
              </div>
            </section>

            {/* Your Rights */}
            <section className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Lock className="w-6 h-6 text-primary" />
                7. Your Rights
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>Under UK GDPR, you have the right to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                  <li><strong>Erasure:</strong> Request deletion of your data (subject to legal requirements)</li>
                  <li><strong>Restriction:</strong> Limit how we use your data</li>
                  <li><strong>Portability:</strong> Receive your data in a portable format</li>
                  <li><strong>Objection:</strong> Object to certain processing activities</li>
                  <li><strong>Withdraw Consent:</strong> Where processing is based on consent</li>
                </ul>
                <p className="mt-4">
                  To exercise these rights, contact us at <a href="mailto:privacy@letwise.io" className="text-primary hover:underline">privacy@letwise.io</a>.
                  We will respond within one month.
                </p>
              </div>
            </section>

            {/* Security */}
            <section className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Data Security</h2>
              <div className="space-y-4 text-slate-600">
                <p>We implement appropriate technical and organisational measures to protect your data, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Secure access controls and authentication</li>
                  <li>Regular security assessments</li>
                  <li>Staff training on data protection</li>
                  <li>Incident response procedures</li>
                </ul>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-white rounded-xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Mail className="w-6 h-6 text-primary" />
                9. Contact Us
              </h2>
              <div className="text-slate-600">
                <p className="mb-4">For privacy-related enquiries or to exercise your rights:</p>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p><strong>Data Protection Contact</strong></p>
                  <p>C&C LET MANAGEMENT LIMITED</p>
                  <p>Unit 4, 416 Hamilton Road, Cambuslang, Glasgow, G72 7XR</p>
                  <p>Email: <a href="mailto:privacy@letwise.io" className="text-primary hover:underline">privacy@letwise.io</a></p>
                </div>
                <p className="mt-4">
                  You also have the right to lodge a complaint with the Information Commissioner's Office (ICO):<br />
                  <a href="https://ico.org.uk" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">www.ico.org.uk</a>
                </p>
              </div>
            </section>

          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
